import { normalizeLocale, type SupportedLocale } from '@/i18n/config';
import { createVietnameseStaticCopy } from '@/i18n/vietnamese-static-copy';

export type DownloadsPageCopy = {
  loadingDownloads: string;
  collectingRecentFilePackages: string;
  loadingDownloadJobs: string;
  fetchingWorkspaceJobs: string;
  workspaceNotFound: string;
  movingToDashboard: string;
  openMenu: string;
  downloads: string;
  preparedFilesExpiry: string;
  all: string;
  preparing: string;
  ready: string;
  needsRetry: string;
  noJobsInView: string;
  downloadPackage: string;
  download: string;
  retry: string;
  cancel: string;
  hide: string;
  openProduct: string;
  menu: string;
  queued: string;
  preparingFiles: string;
  preparingProgress: (current: number, total: number) => string;
  readyToDownload: string;
  preparationFailed: string;
  downloadExpired: string;
  cancelled: string;
  filesLabel: (count: number) => string;
  preparingAgainSuccess: string;
  originalSelectionUnavailable: string;
  preparationAlreadyInProgress: string;
  couldNotPrepareAgain: string;
  noLongerQueued: string;
  cancellationRequested: string;
  cancelRequestFailed: string;
  downloadInitializing: string;
  couldNotCancelDownload: string;
  cancelledPreparation: string;
  hideReadyDownloadConfirm: string;
  couldNotHideJob: string;
};

const EN_COPY: DownloadsPageCopy = {
  loadingDownloads: 'Loading downloads',
  collectingRecentFilePackages: 'Collecting your recent file packages',
  loadingDownloadJobs: 'Loading download jobs',
  fetchingWorkspaceJobs: 'Fetching your workspace jobs',
  workspaceNotFound: 'Workspace not found',
  movingToDashboard: 'Moving you to dashboard...',
  openMenu: 'Open menu',
  downloads: 'Downloads',
  preparedFilesExpiry:
    'Prepared files stay available for 24 hours before expiring.',
  all: 'All',
  preparing: 'Preparing',
  ready: 'Ready',
  needsRetry: 'Needs retry',
  noJobsInView: 'No jobs in this view yet.',
  downloadPackage: 'Download package',
  download: 'Download',
  retry: 'Retry',
  cancel: 'Cancel',
  hide: 'Hide',
  openProduct: 'Open product',
  menu: 'Menu',
  queued: 'Queued',
  preparingFiles: 'Preparing files',
  preparingProgress: (current, total) => `Preparing ${current}/${total}`,
  readyToDownload: 'Ready to download',
  preparationFailed: 'Preparation failed',
  downloadExpired: 'Download expired',
  cancelled: 'Cancelled',
  filesLabel: (count) => `${count} files`,
  preparingAgainSuccess: 'Preparing files again for download',
  originalSelectionUnavailable:
    'Original selection is no longer available. Start a new download from the product page.',
  preparationAlreadyInProgress:
    'Preparation is already in progress for this job',
  couldNotPrepareAgain: 'Could not prepare files again',
  noLongerQueued: 'This download is no longer queued',
  cancellationRequested: 'Cancellation requested. Updating status shortly.',
  cancelRequestFailed: 'Cancel request failed. Please try again.',
  downloadInitializing:
    'Download is still initializing. Try again in a moment.',
  couldNotCancelDownload: 'Could not cancel this download',
  cancelledPreparation: 'Cancelled download preparation',
  hideReadyDownloadConfirm:
    'Hide this ready download? You can prepare these files again later from the product page.',
  couldNotHideJob: 'Could not hide this job',
};

const VI_COPY: DownloadsPageCopy = createVietnameseStaticCopy(EN_COPY);

const ID_COPY: DownloadsPageCopy = {
  loadingDownloads: 'Memuat unduhan',
  collectingRecentFilePackages: 'Mengumpulkan paket file terbaru Anda',
  loadingDownloadJobs: 'Memuat pekerjaan unduhan',
  fetchingWorkspaceJobs: 'Mengambil pekerjaan workspace Anda',
  workspaceNotFound: 'Workspace tidak ditemukan',
  movingToDashboard: 'Mengarahkan Anda ke dashboard...',
  openMenu: 'Buka menu',
  downloads: 'Unduhan',
  preparedFilesExpiry:
    'File yang sudah disiapkan tetap tersedia selama 24 jam sebelum kedaluwarsa.',
  all: 'Semua',
  preparing: 'Menyiapkan',
  ready: 'Siap',
  needsRetry: 'Perlu coba lagi',
  noJobsInView: 'Belum ada pekerjaan di tampilan ini.',
  downloadPackage: 'Unduh paket',
  download: 'Unduh',
  retry: 'Coba lagi',
  cancel: 'Batal',
  hide: 'Sembunyikan',
  openProduct: 'Buka produk',
  menu: 'Menu',
  queued: 'Dalam antrean',
  preparingFiles: 'Menyiapkan file',
  preparingProgress: (current, total) => `Menyiapkan ${current}/${total}`,
  readyToDownload: 'Siap diunduh',
  preparationFailed: 'Penyiapan gagal',
  downloadExpired: 'Unduhan kedaluwarsa',
  cancelled: 'Dibatalkan',
  filesLabel: (count) => `${count} file`,
  preparingAgainSuccess: 'Menyiapkan ulang file untuk diunduh',
  originalSelectionUnavailable:
    'Pilihan asli sudah tidak tersedia. Mulai unduhan baru dari halaman produk.',
  preparationAlreadyInProgress:
    'Penyiapan untuk pekerjaan ini sudah sedang berlangsung',
  couldNotPrepareAgain: 'Tidak dapat menyiapkan ulang file',
  noLongerQueued: 'Unduhan ini tidak lagi berada dalam antrean',
  cancellationRequested:
    'Permintaan pembatalan dikirim. Status akan segera diperbarui.',
  cancelRequestFailed: 'Permintaan pembatalan gagal. Silakan coba lagi.',
  downloadInitializing:
    'Unduhan masih sedang diinisialisasi. Coba lagi sebentar lagi.',
  couldNotCancelDownload: 'Tidak dapat membatalkan unduhan ini',
  cancelledPreparation: 'Penyiapan unduhan dibatalkan',
  hideReadyDownloadConfirm:
    'Sembunyikan unduhan yang sudah siap ini? Anda dapat menyiapkan ulang file-file ini nanti dari halaman produk.',
  couldNotHideJob: 'Tidak dapat menyembunyikan pekerjaan ini',
};

const AR_COPY: DownloadsPageCopy = {
  loadingDownloads: 'جارٍ تحميل التنزيلات',
  collectingRecentFilePackages: 'جارٍ جمع أحدث حِزم الملفات الخاصة بك',
  loadingDownloadJobs: 'جارٍ تحميل مهام التنزيل',
  fetchingWorkspaceJobs: 'جارٍ جلب مهام مساحة العمل الخاصة بك',
  workspaceNotFound: 'لم يتم العثور على مساحة العمل',
  movingToDashboard: 'جارٍ نقلك إلى لوحة التحكم...',
  openMenu: 'فتح القائمة',
  downloads: 'التنزيلات',
  preparedFilesExpiry:
    'تبقى الملفات المُجهزة متاحة لمدة 24 ساعة قبل انتهاء صلاحيتها.',
  all: 'الكل',
  preparing: 'جارٍ التجهيز',
  ready: 'جاهز',
  needsRetry: 'بحاجة إلى إعادة المحاولة',
  noJobsInView: 'لا توجد مهام في هذا العرض بعد.',
  downloadPackage: 'تنزيل الحزمة',
  download: 'تنزيل',
  retry: 'إعادة المحاولة',
  cancel: 'إلغاء',
  hide: 'إخفاء',
  openProduct: 'فتح المنتج',
  menu: 'القائمة',
  queued: 'في الانتظار',
  preparingFiles: 'جارٍ تجهيز الملفات',
  preparingProgress: (current, total) => `جارٍ التجهيز ${current}/${total}`,
  readyToDownload: 'جاهز للتنزيل',
  preparationFailed: 'فشل تجهيز الملفات',
  downloadExpired: 'انتهت صلاحية التنزيل',
  cancelled: 'تم الإلغاء',
  filesLabel: (count) => `${count} ملف`,
  preparingAgainSuccess: 'جارٍ تجهيز الملفات مرة أخرى للتنزيل',
  originalSelectionUnavailable:
    'لم يعد التحديد الأصلي متاحًا. ابدأ تنزيلًا جديدًا من صفحة المنتج.',
  preparationAlreadyInProgress: 'تجهيز هذا التنزيل قيد التنفيذ بالفعل',
  couldNotPrepareAgain: 'تعذر تجهيز الملفات مرة أخرى',
  noLongerQueued: 'لم يعد هذا التنزيل في قائمة الانتظار',
  cancellationRequested: 'تم طلب الإلغاء. سيتم تحديث الحالة قريبًا.',
  cancelRequestFailed: 'فشل طلب الإلغاء. حاول مرة أخرى.',
  downloadInitializing: 'لا يزال التنزيل قيد التهيئة. حاول مرة أخرى بعد لحظة.',
  couldNotCancelDownload: 'تعذر إلغاء هذا التنزيل',
  cancelledPreparation: 'تم إلغاء تجهيز التنزيل',
  hideReadyDownloadConfirm:
    'هل تريد إخفاء هذا التنزيل الجاهز؟ يمكنك تجهيز هذه الملفات مرة أخرى لاحقًا من صفحة المنتج.',
  couldNotHideJob: 'تعذر إخفاء هذه المهمة',
};

const DE_COPY: DownloadsPageCopy = {
  loadingDownloads: 'Downloads werden geladen',
  collectingRecentFilePackages: 'Deine letzten Dateipakete werden gesammelt',
  loadingDownloadJobs: 'Download-Auftraege werden geladen',
  fetchingWorkspaceJobs: 'Deine Workspace-Auftraege werden geladen',
  workspaceNotFound: 'Workspace nicht gefunden',
  movingToDashboard: 'Du wirst zum Dashboard weitergeleitet...',
  openMenu: 'Menue oeffnen',
  downloads: 'Downloads',
  preparedFilesExpiry:
    'Vorbereitete Dateien bleiben 24 Stunden lang verfuegbar, bevor sie ablaufen.',
  all: 'Alle',
  preparing: 'Wird vorbereitet',
  ready: 'Bereit',
  needsRetry: 'Erneut versuchen',
  noJobsInView: 'In dieser Ansicht gibt es noch keine Auftraege.',
  downloadPackage: 'Paket herunterladen',
  download: 'Herunterladen',
  retry: 'Erneut versuchen',
  cancel: 'Abbrechen',
  hide: 'Ausblenden',
  openProduct: 'Produkt oeffnen',
  menu: 'Menue',
  queued: 'In Warteschlange',
  preparingFiles: 'Dateien werden vorbereitet',
  preparingProgress: (current, total) => `Vorbereitung ${current}/${total}`,
  readyToDownload: 'Bereit zum Herunterladen',
  preparationFailed: 'Vorbereitung fehlgeschlagen',
  downloadExpired: 'Download abgelaufen',
  cancelled: 'Abgebrochen',
  filesLabel: (count) => `${count} Dateien`,
  preparingAgainSuccess: 'Dateien werden erneut fuer den Download vorbereitet',
  originalSelectionUnavailable:
    'Die urspruengliche Auswahl ist nicht mehr verfuegbar. Starte einen neuen Download auf der Produktseite.',
  preparationAlreadyInProgress:
    'Die Vorbereitung fuer diesen Auftrag laeuft bereits',
  couldNotPrepareAgain: 'Dateien konnten nicht erneut vorbereitet werden',
  noLongerQueued: 'Dieser Download ist nicht mehr in der Warteschlange',
  cancellationRequested:
    'Abbruch angefordert. Der Status wird in Kuerze aktualisiert.',
  cancelRequestFailed: 'Abbruch fehlgeschlagen. Bitte versuche es erneut.',
  downloadInitializing:
    'Der Download wird noch initialisiert. Versuche es gleich noch einmal.',
  couldNotCancelDownload: 'Dieser Download konnte nicht abgebrochen werden',
  cancelledPreparation: 'Download-Vorbereitung abgebrochen',
  hideReadyDownloadConfirm:
    'Diesen fertigen Download ausblenden? Du kannst diese Dateien spaeter auf der Produktseite erneut vorbereiten.',
  couldNotHideJob: 'Dieser Auftrag konnte nicht ausgeblendet werden',
};

const DE_AT_COPY: DownloadsPageCopy = {
  ...DE_COPY,
};

const ES_COPY: DownloadsPageCopy = {
  loadingDownloads: 'Cargando descargas',
  collectingRecentFilePackages: 'Reuniendo tus paquetes de archivos recientes',
  loadingDownloadJobs: 'Cargando tareas de descarga',
  fetchingWorkspaceJobs: 'Obteniendo las tareas de tu espacio de trabajo',
  workspaceNotFound: 'Espacio de trabajo no encontrado',
  movingToDashboard: 'Llevandote al panel...',
  openMenu: 'Abrir menu',
  downloads: 'Descargas',
  preparedFilesExpiry:
    'Los archivos preparados seguiran disponibles durante 24 horas antes de vencer.',
  all: 'Todo',
  preparing: 'Preparando',
  ready: 'Listo',
  needsRetry: 'Requiere reintento',
  noJobsInView: 'Todavia no hay tareas en esta vista.',
  downloadPackage: 'Descargar paquete',
  download: 'Descargar',
  retry: 'Reintentar',
  cancel: 'Cancelar',
  hide: 'Ocultar',
  openProduct: 'Abrir producto',
  menu: 'Menu',
  queued: 'En cola',
  preparingFiles: 'Preparando archivos',
  preparingProgress: (current, total) => `Preparando ${current}/${total}`,
  readyToDownload: 'Listo para descargar',
  preparationFailed: 'La preparacion fallo',
  downloadExpired: 'La descarga vencio',
  cancelled: 'Cancelado',
  filesLabel: (count) => `${count} archivos`,
  preparingAgainSuccess: 'Preparando archivos otra vez para descargar',
  originalSelectionUnavailable:
    'La seleccion original ya no esta disponible. Inicia una nueva descarga desde la pagina del producto.',
  preparationAlreadyInProgress:
    'La preparacion ya esta en curso para esta tarea',
  couldNotPrepareAgain: 'No se pudieron preparar los archivos otra vez',
  noLongerQueued: 'Esta descarga ya no esta en cola',
  cancellationRequested:
    'Se solicito la cancelacion. El estado se actualizara en breve.',
  cancelRequestFailed: 'La cancelacion fallo. Intentalo de nuevo.',
  downloadInitializing:
    'La descarga aun se esta inicializando. Intentalo de nuevo en un momento.',
  couldNotCancelDownload: 'No se pudo cancelar esta descarga',
  cancelledPreparation: 'Se cancelo la preparacion de la descarga',
  hideReadyDownloadConfirm:
    'Ocultar esta descarga lista? Podras preparar estos archivos otra vez mas tarde desde la pagina del producto.',
  couldNotHideJob: 'No se pudo ocultar esta tarea',
};

const FR_COPY: DownloadsPageCopy = {
  loadingDownloads: 'Chargement des telechargements',
  collectingRecentFilePackages:
    'Recuperation de vos paquets de fichiers recents',
  loadingDownloadJobs: 'Chargement des taches de telechargement',
  fetchingWorkspaceJobs: 'Recuperation des taches de votre espace de travail',
  workspaceNotFound: 'Espace de travail introuvable',
  movingToDashboard: 'Redirection vers le tableau de bord...',
  openMenu: 'Ouvrir le menu',
  downloads: 'Telechargements',
  preparedFilesExpiry:
    'Les fichiers prepares restent disponibles pendant 24 heures avant expiration.',
  all: 'Tous',
  preparing: 'Preparation',
  ready: 'Pret',
  needsRetry: 'Nouvelle tentative requise',
  noJobsInView: "Aucune tache dans cette vue pour l'instant.",
  downloadPackage: 'Telecharger le pack',
  download: 'Telecharger',
  retry: 'Reessayer',
  cancel: 'Annuler',
  hide: 'Masquer',
  openProduct: 'Ouvrir le produit',
  menu: 'Menu',
  queued: 'En attente',
  preparingFiles: 'Preparation des fichiers',
  preparingProgress: (current, total) => `Preparation ${current}/${total}`,
  readyToDownload: 'Pret a telecharger',
  preparationFailed: 'La preparation a echoue',
  downloadExpired: 'Telechargement expire',
  cancelled: 'Annule',
  filesLabel: (count) => `${count} fichiers`,
  preparingAgainSuccess:
    'Preparation des fichiers relancee pour le telechargement',
  originalSelectionUnavailable:
    "La selection d'origine n'est plus disponible. Lancez un nouveau telechargement depuis la page produit.",
  preparationAlreadyInProgress:
    'La preparation est deja en cours pour cette tache',
  couldNotPrepareAgain: 'Impossible de preparer a nouveau les fichiers',
  noLongerQueued: "Ce telechargement n'est plus en file d'attente",
  cancellationRequested:
    'Annulation demandee. Le statut sera mis a jour sous peu.',
  cancelRequestFailed: "La demande d'annulation a echoue. Veuillez reessayer.",
  downloadInitializing:
    'Le telechargement est encore en cours dinitialisation. Reessayez dans un instant.',
  couldNotCancelDownload: "Impossible d'annuler ce telechargement",
  cancelledPreparation: 'Preparation du telechargement annulee',
  hideReadyDownloadConfirm:
    'Masquer ce telechargement pret ? Vous pourrez preparer a nouveau ces fichiers plus tard depuis la page produit.',
  couldNotHideJob: 'Impossible de masquer cette tache',
};

const HI_COPY: DownloadsPageCopy = {
  loadingDownloads: 'डाउनलोड लोड हो रहे हैं',
  collectingRecentFilePackages: 'आपके हाल के फ़ाइल पैकेज इकट्ठा किए जा रहे हैं',
  loadingDownloadJobs: 'डाउनलोड जॉब लोड हो रहे हैं',
  fetchingWorkspaceJobs: 'आपके वर्कस्पेस जॉब लाए जा रहे हैं',
  workspaceNotFound: 'वर्कस्पेस नहीं मिला',
  movingToDashboard: 'आपको डैशबोर्ड पर ले जाया जा रहा है...',
  openMenu: 'मेनू खोलें',
  downloads: 'डाउनलोड',
  preparedFilesExpiry:
    'तैयार की गई फ़ाइलें समाप्त होने से पहले 24 घंटे तक उपलब्ध रहती हैं।',
  all: 'सभी',
  preparing: 'तैयारी हो रही है',
  ready: 'तैयार',
  needsRetry: 'फिर से कोशिश चाहिए',
  noJobsInView: 'इस दृश्य में अभी कोई जॉब नहीं है।',
  downloadPackage: 'पैकेज डाउनलोड करें',
  download: 'डाउनलोड',
  retry: 'फिर कोशिश करें',
  cancel: 'रद्द करें',
  hide: 'छिपाएं',
  openProduct: 'प्रोडक्ट खोलें',
  menu: 'मेनू',
  queued: 'कतार में',
  preparingFiles: 'फ़ाइलें तैयार की जा रही हैं',
  preparingProgress: (current, total) => `तैयारी ${current}/${total}`,
  readyToDownload: 'डाउनलोड के लिए तैयार',
  preparationFailed: 'तैयारी विफल रही',
  downloadExpired: 'डाउनलोड समाप्त हो गया',
  cancelled: 'रद्द किया गया',
  filesLabel: (count) => `${count} फ़ाइलें`,
  preparingAgainSuccess: 'डाउनलोड के लिए फ़ाइलें फिर से तैयार की जा रही हैं',
  originalSelectionUnavailable:
    'मूल चयन अब उपलब्ध नहीं है। प्रोडक्ट पेज से नया डाउनलोड शुरू करें।',
  preparationAlreadyInProgress: 'इस जॉब के लिए तैयारी पहले से चल रही है',
  couldNotPrepareAgain: 'फ़ाइलें फिर से तैयार नहीं की जा सकीं',
  noLongerQueued: 'यह डाउनलोड अब कतार में नहीं है',
  cancellationRequested:
    'रद्द करने का अनुरोध किया गया। स्थिति जल्द अपडेट होगी।',
  cancelRequestFailed: 'रद्द करने का अनुरोध विफल हुआ। फिर से कोशिश करें।',
  downloadInitializing:
    'डाउनलोड अभी शुरू हो रहा है। थोड़ी देर में फिर कोशिश करें।',
  couldNotCancelDownload: 'यह डाउनलोड रद्द नहीं किया जा सका',
  cancelledPreparation: 'डाउनलोड तैयारी रद्द कर दी गई',
  hideReadyDownloadConfirm:
    'क्या इस तैयार डाउनलोड को छिपाना है? आप इन फ़ाइलों को बाद में प्रोडक्ट पेज से फिर तैयार कर सकते हैं।',
  couldNotHideJob: 'इस जॉब को छिपाया नहीं जा सका',
};

const IT_COPY: DownloadsPageCopy = {
  loadingDownloads: 'Caricamento dei download',
  collectingRecentFilePackages: 'Raccolta dei tuoi pacchetti di file recenti',
  loadingDownloadJobs: 'Caricamento dei processi di download',
  fetchingWorkspaceJobs: 'Recupero dei processi del tuo workspace',
  workspaceNotFound: 'Workspace non trovato',
  movingToDashboard: 'Reindirizzamento alla dashboard...',
  openMenu: 'Apri menu',
  downloads: 'Download',
  preparedFilesExpiry:
    'I file preparati restano disponibili per 24 ore prima di scadere.',
  all: 'Tutti',
  preparing: 'Preparazione',
  ready: 'Pronto',
  needsRetry: 'Serve un nuovo tentativo',
  noJobsInView: 'Non ci sono ancora processi in questa vista.',
  downloadPackage: 'Scarica pacchetto',
  download: 'Scarica',
  retry: 'Riprova',
  cancel: 'Annulla',
  hide: 'Nascondi',
  openProduct: 'Apri prodotto',
  menu: 'Menu',
  queued: 'In coda',
  preparingFiles: 'Preparazione dei file',
  preparingProgress: (current, total) => `Preparazione ${current}/${total}`,
  readyToDownload: 'Pronto per il download',
  preparationFailed: 'Preparazione non riuscita',
  downloadExpired: 'Download scaduto',
  cancelled: 'Annullato',
  filesLabel: (count) => `${count} file`,
  preparingAgainSuccess: 'Preparazione dei file riavviata per il download',
  originalSelectionUnavailable:
    'La selezione originale non e piu disponibile. Avvia un nuovo download dalla pagina prodotto.',
  preparationAlreadyInProgress:
    'La preparazione e gia in corso per questo processo',
  couldNotPrepareAgain: 'Impossibile preparare di nuovo i file',
  noLongerQueued: 'Questo download non e piu in coda',
  cancellationRequested:
    'Richiesta di annullamento inviata. Lo stato verra aggiornato a breve.',
  cancelRequestFailed: 'Richiesta di annullamento non riuscita. Riprova.',
  downloadInitializing:
    'Il download e ancora in fase di inizializzazione. Riprova tra un momento.',
  couldNotCancelDownload: 'Impossibile annullare questo download',
  cancelledPreparation: 'Preparazione del download annullata',
  hideReadyDownloadConfirm:
    'Nascondere questo download pronto? Potrai preparare di nuovo questi file piu tardi dalla pagina prodotto.',
  couldNotHideJob: 'Impossibile nascondere questo processo',
};

const JA_COPY: DownloadsPageCopy = {
  loadingDownloads: 'ダウンロードを読み込み中',
  collectingRecentFilePackages: '最近のファイルパッケージを集めています',
  loadingDownloadJobs: 'ダウンロードジョブを読み込み中',
  fetchingWorkspaceJobs: 'ワークスペースのジョブを取得しています',
  workspaceNotFound: 'ワークスペースが見つかりません',
  movingToDashboard: 'ダッシュボードへ移動しています...',
  openMenu: 'メニューを開く',
  downloads: 'ダウンロード',
  preparedFilesExpiry:
    '準備済みファイルは期限切れになるまで24時間利用できます。',
  all: 'すべて',
  preparing: '準備中',
  ready: '準備完了',
  needsRetry: '再試行が必要',
  noJobsInView: 'この表示にはまだジョブがありません。',
  downloadPackage: 'パッケージをダウンロード',
  download: 'ダウンロード',
  retry: '再試行',
  cancel: 'キャンセル',
  hide: '非表示',
  openProduct: '商品を開く',
  menu: 'メニュー',
  queued: 'キュー済み',
  preparingFiles: 'ファイルを準備中',
  preparingProgress: (current, total) => `準備中 ${current}/${total}`,
  readyToDownload: 'ダウンロード可能',
  preparationFailed: '準備に失敗しました',
  downloadExpired: 'ダウンロード期限切れ',
  cancelled: 'キャンセル済み',
  filesLabel: (count) => `${count} 件のファイル`,
  preparingAgainSuccess: 'ダウンロード用にファイルを再度準備しています',
  originalSelectionUnavailable:
    '元の選択はもう利用できません。商品ページから新しいダウンロードを開始してください。',
  preparationAlreadyInProgress: 'このジョブの準備はすでに進行中です',
  couldNotPrepareAgain: 'ファイルを再度準備できませんでした',
  noLongerQueued: 'このダウンロードはもうキューにありません',
  cancellationRequested:
    'キャンセルをリクエストしました。まもなく状態が更新されます。',
  cancelRequestFailed:
    'キャンセルのリクエストに失敗しました。もう一度お試しください。',
  downloadInitializing:
    'ダウンロードはまだ初期化中です。少し待ってからもう一度お試しください。',
  couldNotCancelDownload: 'このダウンロードをキャンセルできませんでした',
  cancelledPreparation: 'ダウンロード準備をキャンセルしました',
  hideReadyDownloadConfirm:
    'この準備完了のダウンロードを非表示にしますか？これらのファイルは後で商品ページから再度準備できます。',
  couldNotHideJob: 'このジョブを非表示にできませんでした',
};

const KO_COPY: DownloadsPageCopy = {
  loadingDownloads: '다운로드를 불러오는 중',
  collectingRecentFilePackages: '최근 파일 패키지를 모으는 중',
  loadingDownloadJobs: '다운로드 작업을 불러오는 중',
  fetchingWorkspaceJobs: '워크스페이스 작업을 가져오는 중',
  workspaceNotFound: '워크스페이스를 찾을 수 없습니다',
  movingToDashboard: '대시보드로 이동하는 중...',
  openMenu: '메뉴 열기',
  downloads: '다운로드',
  preparedFilesExpiry:
    '준비된 파일은 만료되기 전까지 24시간 동안 사용할 수 있습니다.',
  all: '전체',
  preparing: '준비 중',
  ready: '준비 완료',
  needsRetry: '다시 시도 필요',
  noJobsInView: '이 보기에는 아직 작업이 없습니다.',
  downloadPackage: '패키지 다운로드',
  download: '다운로드',
  retry: '다시 시도',
  cancel: '취소',
  hide: '숨기기',
  openProduct: '상품 열기',
  menu: '메뉴',
  queued: '대기 중',
  preparingFiles: '파일 준비 중',
  preparingProgress: (current, total) => `준비 중 ${current}/${total}`,
  readyToDownload: '다운로드 가능',
  preparationFailed: '준비에 실패했습니다',
  downloadExpired: '다운로드가 만료되었습니다',
  cancelled: '취소됨',
  filesLabel: (count) => `${count}개 파일`,
  preparingAgainSuccess: '다운로드를 위해 파일을 다시 준비하는 중입니다',
  originalSelectionUnavailable:
    '원래 선택 항목을 더 이상 사용할 수 없습니다. 상품 페이지에서 새 다운로드를 시작해 주세요.',
  preparationAlreadyInProgress: '이 작업의 준비가 이미 진행 중입니다',
  couldNotPrepareAgain: '파일을 다시 준비할 수 없습니다',
  noLongerQueued: '이 다운로드는 더 이상 대기 중이 아닙니다',
  cancellationRequested: '취소가 요청되었습니다. 곧 상태가 업데이트됩니다.',
  cancelRequestFailed: '취소 요청에 실패했습니다. 다시 시도해 주세요.',
  downloadInitializing:
    '다운로드가 아직 초기화 중입니다. 잠시 후 다시 시도해 주세요.',
  couldNotCancelDownload: '이 다운로드를 취소할 수 없습니다',
  cancelledPreparation: '다운로드 준비가 취소되었습니다',
  hideReadyDownloadConfirm:
    '이 준비된 다운로드를 숨길까요? 나중에 상품 페이지에서 다시 준비할 수 있습니다.',
  couldNotHideJob: '이 작업을 숨길 수 없습니다',
};

const RU_COPY: DownloadsPageCopy = {
  loadingDownloads: 'Загрузка загрузок',
  collectingRecentFilePackages: 'Собираем ваши последние пакеты файлов',
  loadingDownloadJobs: 'Загрузка задач скачивания',
  fetchingWorkspaceJobs: 'Получаем задачи вашего рабочего пространства',
  workspaceNotFound: 'Рабочее пространство не найдено',
  movingToDashboard: 'Переходим на панель...',
  openMenu: 'Открыть меню',
  downloads: 'Загрузки',
  preparedFilesExpiry:
    'Подготовленные файлы остаются доступными 24 часа до истечения срока.',
  all: 'Все',
  preparing: 'Подготовка',
  ready: 'Готово',
  needsRetry: 'Нужно повторить',
  noJobsInView: 'В этом представлении пока нет задач.',
  downloadPackage: 'Скачать пакет',
  download: 'Скачать',
  retry: 'Повторить',
  cancel: 'Отменить',
  hide: 'Скрыть',
  openProduct: 'Открыть продукт',
  menu: 'Меню',
  queued: 'В очереди',
  preparingFiles: 'Подготовка файлов',
  preparingProgress: (current, total) => `Подготовка ${current}/${total}`,
  readyToDownload: 'Готово к скачиванию',
  preparationFailed: 'Подготовка не удалась',
  downloadExpired: 'Срок скачивания истек',
  cancelled: 'Отменено',
  filesLabel: (count) => `${count} файлов`,
  preparingAgainSuccess: 'Файлы снова подготавливаются к скачиванию',
  originalSelectionUnavailable:
    'Исходный выбор больше недоступен. Начните новую загрузку со страницы продукта.',
  preparationAlreadyInProgress: 'Подготовка для этой задачи уже выполняется',
  couldNotPrepareAgain: 'Не удалось снова подготовить файлы',
  noLongerQueued: 'Эта загрузка больше не находится в очереди',
  cancellationRequested: 'Запрос на отмену отправлен. Статус скоро обновится.',
  cancelRequestFailed: 'Не удалось отменить. Попробуйте еще раз.',
  downloadInitializing:
    'Загрузка еще инициализируется. Попробуйте снова через мгновение.',
  couldNotCancelDownload: 'Не удалось отменить эту загрузку',
  cancelledPreparation: 'Подготовка загрузки отменена',
  hideReadyDownloadConfirm:
    'Скрыть эту готовую загрузку? Позже вы сможете снова подготовить эти файлы со страницы продукта.',
  couldNotHideJob: 'Не удалось скрыть эту задачу',
};

const ZH_COPY: DownloadsPageCopy = {
  loadingDownloads: '正在加载下载内容',
  collectingRecentFilePackages: '正在收集你最近的文件包',
  loadingDownloadJobs: '正在加载下载任务',
  fetchingWorkspaceJobs: '正在获取你的工作区任务',
  workspaceNotFound: '未找到工作区',
  movingToDashboard: '正在跳转到仪表盘...',
  openMenu: '打开菜单',
  downloads: '下载',
  preparedFilesExpiry: '已准备好的文件会在过期前保留 24 小时。',
  all: '全部',
  preparing: '准备中',
  ready: '就绪',
  needsRetry: '需要重试',
  noJobsInView: '这个视图里还没有任务。',
  downloadPackage: '下载文件包',
  download: '下载',
  retry: '重试',
  cancel: '取消',
  hide: '隐藏',
  openProduct: '打开产品',
  menu: '菜单',
  queued: '排队中',
  preparingFiles: '正在准备文件',
  preparingProgress: (current, total) => `正在准备 ${current}/${total}`,
  readyToDownload: '可下载',
  preparationFailed: '准备失败',
  downloadExpired: '下载已过期',
  cancelled: '已取消',
  filesLabel: (count) => `${count} 个文件`,
  preparingAgainSuccess: '正在重新准备文件以供下载',
  originalSelectionUnavailable: '原始选择已不可用。请从产品页面重新开始下载。',
  preparationAlreadyInProgress: '这个任务的准备已经在进行中',
  couldNotPrepareAgain: '无法再次准备文件',
  noLongerQueued: '这个下载已不在队列中',
  cancellationRequested: '已请求取消，状态很快会更新。',
  cancelRequestFailed: '取消请求失败。请再试一次。',
  downloadInitializing: '下载仍在初始化中，请稍后再试。',
  couldNotCancelDownload: '无法取消这个下载',
  cancelledPreparation: '已取消下载准备',
  hideReadyDownloadConfirm:
    '要隐藏这个已准备好的下载吗？你之后仍可在产品页面重新准备这些文件。',
  couldNotHideJob: '无法隐藏这个任务',
};

const COPY_BY_LOCALE: Record<SupportedLocale, DownloadsPageCopy> = {
  en: EN_COPY,
  'pt-BR': {
    loadingDownloads: 'Carregando downloads',
    collectingRecentFilePackages: 'Coletando seus pacotes de arquivos recentes',
    loadingDownloadJobs: 'Carregando trabalhos de download',
    fetchingWorkspaceJobs: 'Buscando seus trabalhos no espa\u00E7o de trabalho',
    workspaceNotFound: 'Espa\u00E7o de trabalho n\u00E3o encontrado',
    movingToDashboard: 'Movendo voc\u00EA para o painel...',
    openMenu: 'Abrir menu',
    downloads: 'Transfer\u00EAncias',
    preparedFilesExpiry:
      'Arquivos preparados ficam dispon\u00EDveis 24 horas antes de expirar.',
    all: 'Todos',
    preparing: 'Preparando',
    ready: 'Pronto.',
    needsRetry: 'Precisa tentar novamente.',
    noJobsInView: 'Nenhum trabalho nesta vista ainda.',
    downloadPackage: 'Pacote de download',
    download: 'Baixe',
    retry: 'Tente novamente.',
    cancel: 'Cancelar',
    hide: 'Ocultar',
    openProduct: 'Produto aberto.',
    menu: 'Menu',
    queued: 'Em fila.',
    preparingFiles: 'Preparando arquivos',
    preparingProgress: (current, total) => `Preparando ${current}/${total}`,
    readyToDownload: 'Pronto para baixar',
    preparationFailed: 'A prepara\u00E7\u00E3o falhou.',
    downloadExpired: 'Baixar expirado',
    cancelled: 'Cancelado',
    filesLabel: (count) => `${count} Arquivos`,
    preparingAgainSuccess: 'Preparando arquivos novamente para download',
    originalSelectionUnavailable:
      'A sele\u00E7\u00E3o original n\u00E3o est\u00E1 mais dispon\u00EDvel. Comece um novo download da p\u00E1gina do produto.',
    preparationAlreadyInProgress:
      'A prepara\u00E7\u00E3o j\u00E1 est\u00E1 em andamento para este trabalho.',
    couldNotPrepareAgain:
      'N\u00E3o foi poss\u00EDvel preparar arquivos novamente.',
    noLongerQueued: 'Este download n\u00E3o est\u00E1 mais na fila.',
    cancellationRequested:
      'Cancelamento solicitado. Atualizando status em breve.',
    cancelRequestFailed: 'Cancelar pedido falhou. Por favor, tente de novo.',
    downloadInitializing:
      'O download ainda est\u00E1 inicializando. Tente novamente em um momento.',
    couldNotCancelDownload: 'N\u00E3o foi poss\u00EDvel cancelar este download',
    cancelledPreparation: 'Prepara\u00E7\u00E3o de download cancelada',
    hideReadyDownloadConfirm:
      'Esconder esse download pronto? Voc\u00EA pode preparar esses arquivos mais tarde da p\u00E1gina do produto.',
    couldNotHideJob: 'N\u00E3o poderia esconder este trabalho.',
  },
  ar: AR_COPY,
  de: DE_AT_COPY,
  es: ES_COPY,
  fr: FR_COPY,
  hi: HI_COPY,
  it: IT_COPY,
  ja: JA_COPY,
  ko: KO_COPY,
  ru: RU_COPY,
  zh: ZH_COPY,
  'zh-TW': ZH_COPY,
  vi: VI_COPY,
  id: ID_COPY,
};

export type UnifiedDownloadsPageCopy = DownloadsPageCopy & {
  collectingRecentDownloads: string;
  assetPack: string;
  folderArchive: string;
  resolving: string;
  packing: string;
  finalizing: string;
  uploading: string;
  createdAt: (relativeTime: string) => string;
  updatedAt: (relativeTime: string) => string;
  progressPercent: (percent: number) => string;
  skippedCount: (count: number) => string;
  failedCount: (count: number) => string;
  loadMore: string;
  loadingMore: string;
  couldNotDownload: string;
};

type UnifiedOnlyCopy = Omit<UnifiedDownloadsPageCopy, keyof DownloadsPageCopy>;

const EN_UNIFIED_COPY: UnifiedOnlyCopy = {
  collectingRecentDownloads: 'Collecting your recent downloads',
  assetPack: 'Asset pack',
  folderArchive: 'Folder archive',
  resolving: 'Resolving',
  packing: 'Packing',
  finalizing: 'Finalizing',
  uploading: 'Uploading',
  createdAt: (time) => `Created ${time}`,
  updatedAt: (time) => `Updated ${time}`,
  progressPercent: (percent) => `${percent}% complete`,
  skippedCount: (count) => `${count} skipped`,
  failedCount: (count) => `${count} failed`,
  loadMore: 'Load more',
  loadingMore: 'Loading more',
  couldNotDownload: 'Could not download this archive',
};

const UNIFIED_COPY_BY_LOCALE: Record<SupportedLocale, UnifiedOnlyCopy> = {
  en: EN_UNIFIED_COPY,
  'pt-BR': {
    collectingRecentDownloads: 'Recolhendo seus downloads recentes',
    assetPack: 'Pacote de ativos.',
    folderArchive: 'Arquivo de pastas',
    resolving: 'Resolvendo',
    packing: 'Embalar.',
    finalizing: 'Finalizando.',
    uploading: 'Enviando',
    createdAt: (time) => `Criado ${time}`,
    updatedAt: (time) => `Actualizado ${time}`,
    progressPercent: (percent) => `${percent}% completo`,
    skippedCount: (count) => `${count} Saltou.`,
    failedCount: (count) => `${count} Falhou.`,
    loadMore: 'Carregar mais.',
    loadingMore: 'Carregando mais.',
    couldNotDownload: 'N\u00E3o foi poss\u00EDvel baixar este arquivo',
  },
  vi: createVietnameseStaticCopy(EN_UNIFIED_COPY),
  id: {
    ...EN_UNIFIED_COPY,
    collectingRecentDownloads: 'Mengumpulkan unduhan terbaru Anda',
    assetPack: 'Paket aset',
    folderArchive: 'Arsip folder',
    resolving: 'Menelusuri',
    packing: 'Mengemas',
    finalizing: 'Menyelesaikan',
    uploading: 'Mengunggah',
    createdAt: (time) => `Dibuat ${time}`,
    updatedAt: (time) => `Diperbarui ${time}`,
    progressPercent: (percent) => `${percent}% selesai`,
    skippedCount: (count) => `${count} dilewati`,
    failedCount: (count) => `${count} gagal`,
    loadMore: 'Muat lebih banyak',
    loadingMore: 'Memuat lebih banyak',
    couldNotDownload: 'Tidak dapat mengunduh arsip ini',
  },
  ar: {
    ...EN_UNIFIED_COPY,
    collectingRecentDownloads: 'جارٍ جمع أحدث التنزيلات',
    assetPack: 'حزمة أصول',
    folderArchive: 'أرشيف مجلد',
    resolving: 'جارٍ التحقق',
    packing: 'جارٍ الضغط',
    finalizing: 'جارٍ الإنهاء',
    uploading: 'جارٍ الرفع',
    createdAt: (time) => `تم الإنشاء ${time}`,
    updatedAt: (time) => `تم التحديث ${time}`,
    progressPercent: (percent) => `اكتمل ${percent}%`,
    skippedCount: (count) => `تم تخطي ${count}`,
    failedCount: (count) => `فشل ${count}`,
    loadMore: 'تحميل المزيد',
    loadingMore: 'جارٍ تحميل المزيد',
    couldNotDownload: 'تعذر تنزيل هذا الأرشيف',
  },
  de: {
    ...EN_UNIFIED_COPY,
    collectingRecentDownloads: 'Deine letzten Downloads werden geladen',
    assetPack: 'Asset-Paket',
    folderArchive: 'Ordnerarchiv',
    resolving: 'Wird aufgelöst',
    packing: 'Wird gepackt',
    finalizing: 'Wird abgeschlossen',
    uploading: 'Wird hochgeladen',
    createdAt: (time) => `Erstellt ${time}`,
    updatedAt: (time) => `Aktualisiert ${time}`,
    progressPercent: (percent) => `${percent}% abgeschlossen`,
    skippedCount: (count) => `${count} übersprungen`,
    failedCount: (count) => `${count} fehlgeschlagen`,
    loadMore: 'Mehr laden',
    loadingMore: 'Weitere werden geladen',
    couldNotDownload: 'Dieses Archiv konnte nicht geladen werden',
  },
  es: {
    ...EN_UNIFIED_COPY,
    collectingRecentDownloads: 'Recopilando tus descargas recientes',
    assetPack: 'Paquete de recursos',
    folderArchive: 'Archivo de carpeta',
    resolving: 'Resolviendo',
    packing: 'Comprimiendo',
    finalizing: 'Finalizando',
    uploading: 'Subiendo',
    createdAt: (time) => `Creado ${time}`,
    updatedAt: (time) => `Actualizado ${time}`,
    progressPercent: (percent) => `${percent}% completado`,
    skippedCount: (count) => `${count} omitidos`,
    failedCount: (count) => `${count} fallidos`,
    loadMore: 'Cargar más',
    loadingMore: 'Cargando más',
    couldNotDownload: 'No se pudo descargar este archivo',
  },
  fr: {
    ...EN_UNIFIED_COPY,
    collectingRecentDownloads: 'Récupération de vos téléchargements récents',
    assetPack: 'Pack de ressources',
    folderArchive: 'Archive de dossier',
    resolving: 'Résolution',
    packing: 'Compression',
    finalizing: 'Finalisation',
    uploading: 'Téléversement',
    createdAt: (time) => `Créé ${time}`,
    updatedAt: (time) => `Mis à jour ${time}`,
    progressPercent: (percent) => `${percent}% terminé`,
    skippedCount: (count) => `${count} ignorés`,
    failedCount: (count) => `${count} échoués`,
    loadMore: 'Charger plus',
    loadingMore: 'Chargement en cours',
    couldNotDownload: 'Impossible de télécharger cette archive',
  },
  hi: {
    ...EN_UNIFIED_COPY,
    collectingRecentDownloads: 'आपके हाल के डाउनलोड इकट्ठा किए जा रहे हैं',
    assetPack: 'एसेट पैक',
    folderArchive: 'फ़ोल्डर आर्काइव',
    resolving: 'फ़ाइलें खोजी जा रही हैं',
    packing: 'पैक किया जा रहा है',
    finalizing: 'अंतिम रूप दिया जा रहा है',
    uploading: 'अपलोड किया जा रहा है',
    createdAt: (time) => `${time} बनाया गया`,
    updatedAt: (time) => `${time} अपडेट किया गया`,
    progressPercent: (percent) => `${percent}% पूरा`,
    skippedCount: (count) => `${count} छोड़े गए`,
    failedCount: (count) => `${count} विफल`,
    loadMore: 'और लोड करें',
    loadingMore: 'और लोड हो रहे हैं',
    couldNotDownload: 'यह आर्काइव डाउनलोड नहीं किया जा सका',
  },
  it: {
    ...EN_UNIFIED_COPY,
    collectingRecentDownloads: 'Raccolta dei download recenti',
    assetPack: 'Pacchetto risorse',
    folderArchive: 'Archivio cartella',
    resolving: 'Risoluzione',
    packing: 'Compressione',
    finalizing: 'Finalizzazione',
    uploading: 'Caricamento',
    createdAt: (time) => `Creato ${time}`,
    updatedAt: (time) => `Aggiornato ${time}`,
    progressPercent: (percent) => `${percent}% completato`,
    skippedCount: (count) => `${count} ignorati`,
    failedCount: (count) => `${count} non riusciti`,
    loadMore: 'Carica altro',
    loadingMore: 'Caricamento in corso',
    couldNotDownload: 'Impossibile scaricare questo archivio',
  },
  ja: {
    ...EN_UNIFIED_COPY,
    collectingRecentDownloads: '最近のダウンロードを取得しています',
    assetPack: 'アセットパック',
    folderArchive: 'フォルダーアーカイブ',
    resolving: '確認中',
    packing: '圧縮中',
    finalizing: '仕上げ中',
    uploading: 'アップロード中',
    createdAt: (time) => `${time}に作成`,
    updatedAt: (time) => `${time}に更新`,
    progressPercent: (percent) => `${percent}% 完了`,
    skippedCount: (count) => `${count} 件スキップ`,
    failedCount: (count) => `${count} 件失敗`,
    loadMore: 'さらに読み込む',
    loadingMore: '読み込み中',
    couldNotDownload: 'このアーカイブをダウンロードできませんでした',
  },
  ko: {
    ...EN_UNIFIED_COPY,
    collectingRecentDownloads: '최근 다운로드를 불러오는 중',
    assetPack: '에셋 팩',
    folderArchive: '폴더 아카이브',
    resolving: '확인 중',
    packing: '압축 중',
    finalizing: '마무리 중',
    uploading: '업로드 중',
    createdAt: (time) => `${time} 생성`,
    updatedAt: (time) => `${time} 업데이트`,
    progressPercent: (percent) => `${percent}% 완료`,
    skippedCount: (count) => `${count}개 건너뜀`,
    failedCount: (count) => `${count}개 실패`,
    loadMore: '더 불러오기',
    loadingMore: '더 불러오는 중',
    couldNotDownload: '이 아카이브를 다운로드할 수 없습니다',
  },
  ru: {
    ...EN_UNIFIED_COPY,
    collectingRecentDownloads: 'Получаем последние загрузки',
    assetPack: 'Пакет ресурсов',
    folderArchive: 'Архив папки',
    resolving: 'Подготовка списка',
    packing: 'Упаковка',
    finalizing: 'Завершение',
    uploading: 'Отправка',
    createdAt: (time) => `Создано ${time}`,
    updatedAt: (time) => `Обновлено ${time}`,
    progressPercent: (percent) => `Готово на ${percent}%`,
    skippedCount: (count) => `Пропущено: ${count}`,
    failedCount: (count) => `Ошибок: ${count}`,
    loadMore: 'Загрузить ещё',
    loadingMore: 'Загрузка',
    couldNotDownload: 'Не удалось скачать этот архив',
  },
  zh: {
    ...EN_UNIFIED_COPY,
    collectingRecentDownloads: '正在获取你最近的下载',
    assetPack: '资源包',
    folderArchive: '文件夹归档',
    resolving: '正在解析',
    packing: '正在打包',
    finalizing: '正在完成',
    uploading: '正在上传',
    createdAt: (time) => `创建于${time}`,
    updatedAt: (time) => `更新于${time}`,
    progressPercent: (percent) => `已完成 ${percent}%`,
    skippedCount: (count) => `已跳过 ${count}`,
    failedCount: (count) => `失败 ${count}`,
    loadMore: '加载更多',
    loadingMore: '正在加载更多',
    couldNotDownload: '无法下载此归档',
  },
  'zh-TW': {
    ...EN_UNIFIED_COPY,
    collectingRecentDownloads: '正在取得你最近的下載',
    assetPack: '資源包',
    folderArchive: '資料夾封存',
    resolving: '正在解析',
    packing: '正在打包',
    finalizing: '正在完成',
    uploading: '正在上傳',
    createdAt: (time) => `建立於${time}`,
    updatedAt: (time) => `更新於${time}`,
    progressPercent: (percent) => `已完成 ${percent}%`,
    skippedCount: (count) => `已略過 ${count}`,
    failedCount: (count) => `失敗 ${count}`,
    loadMore: '載入更多',
    loadingMore: '正在載入更多',
    couldNotDownload: '無法下載此封存檔',
  },
};

export function getDownloadsPageCopy(
  locale?: string | null
): UnifiedDownloadsPageCopy {
  const normalizedLocale = normalizeLocale(locale);
  return {
    ...COPY_BY_LOCALE[normalizedLocale],
    ...UNIFIED_COPY_BY_LOCALE[normalizedLocale],
  };
}
